import * as Dialog from "@radix-ui/react-dialog";
import { type VariantProps, cva } from "class-variance-authority";
import React, { useRef } from "react";

import { CrossIcon } from "@/assets/icons";

// The overlay scrolls and holds the dialog, so a dialog taller than the viewport scrolls with the page
const wrapperVariants = cva("absolute left-1/2 top-0 flex -translate-x-1/2 outline-hidden", {
  variants: {
    size: {
      default: "h-fit max-w-[80%] py-[60px]",
      // A width of its own: sized by its content, it widens past the panel and the cross drifts off
      narrow: "h-fit w-[min(500px,80%)] py-[60px]",
      fullHeight: "h-dvh max-w-none py-8",
    },
  },
  defaultVariants: { size: "default" },
});

const panelVariants = cva(
  "relative rounded-lg bg-card px-8 py-6 text-sm leading-normal text-foreground",
  {
    variants: {
      size: {
        default: "w-[600px]",
        narrow: "w-full",
        // The list scrolls inside the dialog, so the footer with its actions stays in sight
        fullHeight: "flex h-full w-[min(960px,calc(100vw-32px))] flex-col",
      },
    },
    defaultVariants: { size: "default" },
  },
);

// Sits above the panel's corner, on the dark overlay; a full-height panel has no room there
const closeButtonVariants = cva(
  "absolute z-10 flex h-10 w-10 items-center justify-center rounded-lg transition-colors hover:text-primary-fg focus-visible:text-primary-fg focus-visible:outline-dotted focus-visible:outline-1 focus-visible:outline-border",
  {
    variants: {
      size: {
        default: "-right-3 top-4 text-white",
        narrow: "-right-3 top-4 text-white",
        fullHeight: "right-6 top-[52px] text-muted-foreground",
      },
    },
    defaultVariants: { size: "default" },
  },
);

interface ModalProps extends VariantProps<typeof panelVariants> {
  isOpen: boolean;
  onRequestClose: () => void;
  /** The dialog's accessible name; screen readers announce it on open. */
  label: string;
  children: React.ReactNode;
}

export const Modal = ({ isOpen, onRequestClose, label, size, children }: ModalProps) => {
  // Radix returns focus only to a Dialog.Trigger; callers open the modal through `isOpen` instead
  const returnFocusTo = useRef<HTMLElement | null>(null);

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onRequestClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-1001 overflow-y-auto bg-black/70 data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in">
          <Dialog.Content
            aria-describedby={undefined}
            className={wrapperVariants({ size })}
            onOpenAutoFocus={() => {
              returnFocusTo.current = document.activeElement as HTMLElement | null;
            }}
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              returnFocusTo.current?.focus();
            }}
          >
            <Dialog.Title className="sr-only">{label}</Dialog.Title>
            <article className={panelVariants({ size })}>{children}</article>
            {/* After the content, so opening focuses the dialog's own first control, not the cross */}
            <Dialog.Close title="Close modal" className={closeButtonVariants({ size })}>
              <CrossIcon />
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
